import { useState, useRef, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { toast } from "sonner";
import { Microphone, Stop, Spinner, CheckCircle, Warning, Clock, Lock, ArrowUp } from "phosphor-react";
import SmartToolsUpgradeCTA from "./SmartToolsUpgradeCTA";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Max recording duration in seconds
const MAX_DURATION = 120;

export function VoiceNotesCard({ visitId, onNoteAdded, t = (s) => s }) {
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [voiceNotes, setVoiceNotes] = useState([]);
  const [pollingNoteId, setPollingNoteId] = useState(null);
  
  // Feature gating state
  const [aiCapabilities, setAiCapabilities] = useState(null);
  const [capabilitiesLoaded, setCapabilitiesLoaded] = useState(false);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  const streamRef = useRef(null);

  // Fetch AI capabilities on mount
  useEffect(() => {
    fetchAiCapabilities();
  }, []);

  // Fetch existing voice notes
  useEffect(() => {
    fetchVoiceNotes();
  }, [visitId]);

  const fetchAiCapabilities = async () => {
    try {
      const res = await fetch(`${API}/ai/capabilities`, { credentials: "include" });
      if (res.ok) {
        const data = await res.json();
        setAiCapabilities(data);
      }
    } catch (err) {
      console.error("Error fetching AI capabilities:", err);
    } finally {
      setCapabilitiesLoaded(true);
    }
  };

  // Poll for processing status
  useEffect(() => {
    if (!pollingNoteId) return;
    
    const pollInterval = setInterval(async () => {
      try {
        const res = await fetch(
          `${API}/tech/visits/${visitId}/voice-notes/${pollingNoteId}`,
          { credentials: "include" }
        );
        if (res.ok) {
          const note = await res.json();
          if (note.status === "completed" || note.status === "failed") {
            setPollingNoteId(null);
            setIsProcessing(false);
            fetchVoiceNotes();
            
            if (note.status === "completed") {
              toast.success(t("Voice note processed successfully!"));
              if (onNoteAdded) onNoteAdded();
            } else {
              toast.error(t("Voice note processing failed. Please try again."));
            }
          }
        }
      } catch (err) {
        console.error("Polling error:", err);
      }
    }, 2000);
    
    return () => clearInterval(pollInterval);
  }, [pollingNoteId, visitId, onNoteAdded, t]);

  const fetchVoiceNotes = async () => {
    try {
      const res = await fetch(
        `${API}/tech/visits/${visitId}/voice-notes`,
        { credentials: "include" }
      );
      if (res.ok) {
        const data = await res.json();
        setVoiceNotes(data.voice_notes || []);
      }
    } catch (err) {
      console.error("Error fetching voice notes:", err);
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
        }
      });
      streamRef.current = stream;
      
      // Try webm first, fall back to mp4
      const mimeType = MediaRecorder.isTypeSupported("audio/webm;codecs=opus")
        ? "audio/webm;codecs=opus"
        : "audio/mp4";
      
      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: mimeType });
        uploadAudio(audioBlob, recordingDuration);
      };
      
      mediaRecorder.start(1000); // Collect data every second
      setIsRecording(true);
      setRecordingDuration(0);
      
      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingDuration((prev) => {
          if (prev >= MAX_DURATION - 1) {
            stopRecording();
            return MAX_DURATION;
          }
          return prev + 1;
        });
      }, 1000);
      
    } catch (err) {
      console.error("Error starting recording:", err);
      toast.error(t("Could not access microphone. Please check permissions."));
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
        streamRef.current = null;
      }
    }
  };

  const uploadAudio = async (audioBlob, duration) => {
    setIsProcessing(true);
    
    const formData = new FormData();
    formData.append("file", audioBlob, "voice_note.webm");
    formData.append("duration_seconds", duration.toString());
    
    try {
      const res = await fetch(
        `${API}/tech/visits/${visitId}/voice-notes`,
        {
          method: "POST",
          credentials: "include",
          body: formData,
        }
      );
      
      if (res.ok) {
        const data = await res.json();
        toast.success(t("Recording uploaded. Transcribing..."));
        setPollingNoteId(data.note_id);
      } else {
        const err = await res.json();
        toast.error(err.detail || t("Failed to upload recording"));
        setIsProcessing(false);
      }
    } catch (err) {
      console.error("Upload error:", err);
      toast.error(t("Failed to upload recording"));
      setIsProcessing(false);
    }
  };

  const formatDuration = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  const formatTimestamp = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  };

  // Latest completed note
  const latestNote = voiceNotes.find(n => n.status === "completed");
  const hasProcessingNote = voiceNotes.some(n => n.status === "processing") || isProcessing;
  
  // Feature gating with trial support
  const voiceNotesEnabled = aiCapabilities?.voice_notes?.enabled ?? true;
  const upgradeCta = aiCapabilities?.upgrade_cta;
  const trialRemaining = aiCapabilities?.voice_notes?.trial_remaining;
  const trialLimit = aiCapabilities?.voice_notes?.trial_limit;
  const hasSmartTools = aiCapabilities?.has_smart_tools ?? false;
  const isOnTrial = !hasSmartTools && trialRemaining !== null && trialRemaining > 0;

  // If smart_tools not available AND trial exhausted, show upgrade prompt
  if (capabilitiesLoaded && !voiceNotesEnabled) {
    return (
      <Card className="border-2 border-zinc-200" data-testid="voice-notes-card">
        <CardHeader className="pb-2">
          <CardTitle className="font-heading text-lg uppercase flex items-center gap-2">
            <Microphone size={20} />
            {t("Voice Notes")}
            <Badge className="bg-amber-100 text-amber-700 text-xs ml-2">
              <Lock size={12} className="mr-1" />
              {t("Premium")}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <SmartToolsUpgradeCTA
            location="voice_notes"
            trialRemaining={0}
            trialLimit={trialLimit}
            compact={false}
          />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-2 border-zinc-200" data-testid="voice-notes-card">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="font-heading text-lg uppercase flex items-center gap-2">
            <Microphone size={20} />
            {t("Voice Notes")}
          </CardTitle>
          {/* Show trial info badge if on trial */}
          {isOnTrial && (
            <SmartToolsUpgradeCTA
              location="voice_notes"
              trialRemaining={trialRemaining}
              trialLimit={trialLimit}
              compact={true}
              showTrialInfo={true}
            />
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Recording Controls */}
        <div className="flex items-center gap-3">
          {!isRecording && !isProcessing ? (
            <Button
              onClick={startRecording}
              className="bg-[#FF6600] hover:bg-[#CC5200] text-white"
              data-testid="start-recording-btn"
            >
              <Microphone size={18} className="mr-2" />
              {t("Record Voice Note")}
            </Button>
          ) : isRecording ? (
            <div className="flex items-center gap-3 flex-1">
              <Button
                onClick={stopRecording}
                variant="destructive"
                className="bg-red-500 hover:bg-red-600"
                data-testid="stop-recording-btn"
              >
                <Stop size={18} className="mr-2" />
                {t("Stop")}
              </Button>
              <div className="flex items-center gap-2 text-red-500">
                <span className="animate-pulse">●</span>
                <span className="font-mono text-lg">
                  {formatDuration(recordingDuration)}
                </span>
                <span className="text-xs text-zinc-400">
                  / {formatDuration(MAX_DURATION)}
                </span>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-zinc-500">
              <Spinner size={18} className="animate-spin" />
              <span>{t("Transcribing...")}</span>
            </div>
          )}
        </div>

        {/* Processing indicator */}
        {hasProcessingNote && !isRecording && (
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-sm flex items-center gap-2">
            <Spinner size={16} className="animate-spin text-amber-600" />
            <span className="text-sm text-amber-700">
              {t("Processing voice note...")}
            </span>
          </div>
        )}

        {/* Latest Note Display */}
        {latestNote && (
          <div className="p-4 bg-zinc-50 border border-zinc-200 rounded-sm space-y-3">
            <div className="flex items-center justify-between">
              <Badge className="bg-emerald-100 text-emerald-700">
                <CheckCircle size={14} className="mr-1" />
                {t("Latest Note")}
              </Badge>
              <span className="text-xs text-zinc-400">
                {formatTimestamp(latestNote.created_at)}
              </span>
            </div>
            
            {/* AI Summary */}
            {latestNote.ai_summary && (
              <div>
                <p className="text-xs text-zinc-500 uppercase mb-1">{t("Summary")}</p>
                <div className="text-sm whitespace-pre-wrap text-zinc-700">
                  {latestNote.ai_summary}
                </div>
              </div>
            )}
            
            {/* Transcript (collapsible) */}
            {latestNote.raw_transcript && (
              <details className="group">
                <summary className="text-xs text-zinc-500 uppercase cursor-pointer hover:text-zinc-700">
                  {t("View Transcript")}
                </summary>
                <div className="mt-2 p-2 bg-white border border-zinc-200 rounded text-sm text-zinc-600 max-h-40 overflow-y-auto">
                  {latestNote.raw_transcript}
                </div>
              </details>
            )}
          </div>
        )}

        {/* Previous Notes */}
        {voiceNotes.filter(n => n.note_id !== latestNote?.note_id && n.status === "completed").length > 0 && (
          <details className="group">
            <summary className="text-sm text-zinc-500 cursor-pointer hover:text-zinc-700">
              {t("Previous voice notes")} ({voiceNotes.filter(n => n.note_id !== latestNote?.note_id && n.status === "completed").length})
            </summary>
            <div className="mt-2 space-y-2">
              {voiceNotes
                .filter(n => n.note_id !== latestNote?.note_id && n.status === "completed")
                .map((note) => (
                  <div key={note.note_id} className="p-3 bg-zinc-50 border border-zinc-200 rounded-sm">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-xs text-zinc-400">
                        {formatTimestamp(note.created_at)}
                      </span>
                    </div>
                    {note.ai_summary && (
                      <p className="text-sm text-zinc-600">{note.ai_summary}</p>
                    )}
                  </div>
                ))}
            </div>
          </details>
        )}

        {/* Empty state */}
        {voiceNotes.length === 0 && !isRecording && !isProcessing && (
          <p className="text-sm text-zinc-400 text-center py-2">
            {t("No voice notes yet. Tap the button above to record.")}
          </p>
        )}
      </CardContent>
    </Card>
  );
}

export default VoiceNotesCard;
